import { FC } from 'react';

import { Invoice } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { useInvoiceDocument } from '../../../../../hooks/use-invoice-document.hook';

type Props = {
   invoice: Invoice;
   showDivider: boolean;
};

export const ClosedInvoiceCard: FC<Props> = ({ invoice, showDivider }) => {
   const { handleDownload, handleView } = useInvoiceDocument();
   const translate = useTranslations();
   const format = useFormatter();

   const invoicePayload = invoice.documentId
      ? { invoiceId: invoice.id, documentId: invoice.documentId, date: invoice.createDate, type: invoice.type, eInvoiceId: invoice.eInvoiceId }
      : null;

   return (
      <Box direction="column" gap="md">
         {showDivider && <Divider />}
         <Box justify="between" gap="none" items="center">
            <Typography variant="body-highlight" color="strong">
               {format.price(invoice.billedSum)}
            </Typography>
            {invoicePayload && (
               <Box gap="xs">
                  <IconButton icon="download" ariaLabel="Download Invoice" onClick={() => handleDownload(invoicePayload)} variant="on-light" />
                  <IconButton icon="eye" ariaLabel="View Invoice" onClick={() => handleView(invoicePayload)} variant="on-light" />
               </Box>
            )}
         </Box>
         <Box gap="none">
            <Box minWidth="50%">
               <Typography color="subtle">{translate('invoices.closed-invoices.table.table-header.type')}</Typography>
            </Box>
            <Box direction="column" gap="3xs" wordBreak="all">
               <Typography>{invoice.eInvoiceId || invoice.id}</Typography>
               <Typography color="subtle" variant="small">
                  {translate(`invoices.invoice-type.${invoice.type}`)}
               </Typography>
            </Box>
         </Box>
         <Box gap="none">
            <Box minWidth="50%">
               <Typography color="subtle">{translate('invoices.closed-invoices.table.table-header.date')}</Typography>
            </Box>
            <Box>
               <Typography>{format.dateTime(invoice.createDate)}</Typography>
            </Box>
         </Box>
         <Box gap="none">
            <Box minWidth="50%">
               <Typography color="subtle">{translate('invoices.closed-invoices.table.table-header.legal-entity')}</Typography>
            </Box>
            <Box>
               <Typography>{invoice.legalEntity}</Typography>
            </Box>
         </Box>
      </Box>
   );
};
